import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { Loader2, Pause, Play, RefreshCw, Rss } from 'lucide-react';
import {
  ehrErrorMessage,
  type IntegrationResponse,
  type IntegrationStreamName,
  type IntegrationSubscriptionView,
} from '@vagusx/clients';
import { getIntegrationClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  FEED_LABELS,
  STREAM_LABELS,
  STREAM_STATUS_BADGE,
  defaultSelection,
  describeRunResults,
  shouldPoll,
  subscriptionAction,
  subscriptionBadge,
  unavailableReason,
  type Tone,
} from './stream-sync-model';

/** How often the dialog re-reads while a run or a subscription change is in progress. */
const POLL_MS = 10_000;

const TONE_VARIANT: Record<Tone, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  ok: 'default',
  warn: 'outline',
  error: 'destructive',
  muted: 'secondary',
};

function ago(iso: string | null): string {
  return iso ? formatDistanceToNow(new Date(iso), { addSuffix: true }) : 'never';
}

/**
 * The Sync dialog for integrations that run as streams (ADR-299 §3.5): pick which
 * syncs to run now, pause or resume each, and manage the EHR change-feed
 * subscriptions. Syncs that re-read slots or appointments in bulk start unticked.
 */
export function StreamSyncDialog({
  open,
  onOpenChange,
  integration,
  displayName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  integration: IntegrationResponse;
  displayName: string;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const client = getIntegrationClient();
  const id = integration.id;

  const streamsQuery = useQuery({
    queryKey: queryKeys.integrations.streams(id),
    queryFn: () => client.getStreams(id),
    enabled: open,
  });
  const subscriptionsQuery = useQuery({
    queryKey: queryKeys.integrations.subscriptions(id),
    queryFn: () => client.getSubscriptions(id),
    enabled: open,
  });
  const streams = streamsQuery.data?.streams;
  const subscriptions = subscriptionsQuery.data?.subscriptions;
  const polling = shouldPoll(streams, subscriptions);

  useEffect(() => {
    if (!open || !polling) return undefined;
    const timer = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.integrations.streams(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.integrations.subscriptions(id) });
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [open, polling, queryClient, id]);

  // Default ticks once the streams arrive; the user's changes win after that.
  const [selected, setSelected] = useState<Set<IntegrationStreamName> | null>(null);
  useEffect(() => {
    if (streams && selected === null) setSelected(defaultSelection(streams));
  }, [streams, selected]);
  const chosen = selected ?? new Set<IntegrationStreamName>();

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.integrations.streams(id) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.integrations.subscriptions(id) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.integrations.list() });
  };

  const runMutation = useMutation({
    mutationFn: (names: IntegrationStreamName[]) => client.runStreams(id, names),
    onSuccess: (result) => {
      const t = describeRunResults(result);
      toast[t.kind](t.title, t.description ? { description: t.description } : undefined);
      invalidate();
      if (t.kind !== 'error') onOpenChange(false);
    },
  });

  const pauseMutation = useMutation({
    mutationFn: ({ stream, enabled }: { stream: IntegrationStreamName; enabled: boolean }) =>
      client.setStreamEnabled(id, stream, enabled),
    onSuccess: (view) => {
      toast.success(`${STREAM_LABELS[view.stream].title} ${view.enabled ? 'resumed' : 'paused'}`);
      invalidate();
    },
  });

  const checkMutation = useMutation({
    mutationFn: () => client.getSubscriptions(id, true),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.integrations.subscriptions(id), data);
    },
  });

  const subscribeMutation = useMutation({
    mutationFn: ({
      feed,
      desired,
    }: {
      feed: IntegrationSubscriptionView['feed'];
      desired: 'subscribed' | 'unsubscribed';
    }) => client.setSubscription(id, feed, desired),
    onSuccess: (view) => {
      toast.success(
        `${FEED_LABELS[view.feed]} ${view.desired === 'subscribed' ? 'subscription' : 'unsubscribe'} requested`,
        { description: 'The EHR applies it in 10–20 minutes.' }
      );
      invalidate();
    },
  });

  const [confirmUnsubscribe, setConfirmUnsubscribe] = useState<IntegrationSubscriptionView | null>(
    null
  );

  const toggle = (stream: IntegrationStreamName, on: boolean): void => {
    const next = new Set(chosen);
    if (on) next.add(stream);
    else next.delete(stream);
    setSelected(next);
  };

  const loading = streamsQuery.isLoading || subscriptionsQuery.isLoading;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Sync {displayName}</DialogTitle>
            <DialogDescription>
              Choose what to sync now. Bulk re-reads of slots and appointments start unticked.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading syncs…
            </div>
          ) : (
            <div className="space-y-5 py-1">
              <section className="space-y-2">
                <h3 className="text-sm font-medium">Syncs</h3>
                {(streams ?? []).length === 0 && (
                  <p className="text-sm text-muted-foreground">No syncs for this integration.</p>
                )}
                {(streams ?? []).map((s) => {
                  const reason = unavailableReason(s);
                  const badge = STREAM_STATUS_BADGE[s.status];
                  return (
                    <div key={s.stream} className="flex items-start gap-3 rounded-md border p-3">
                      <Checkbox
                        className="mt-0.5"
                        checked={chosen.has(s.stream)}
                        disabled={reason !== null}
                        onCheckedChange={(checked) => toggle(s.stream, checked === true)}
                        aria-label={`Run ${STREAM_LABELS[s.stream].title}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">
                            {STREAM_LABELS[s.stream].title}
                          </span>
                          <Badge variant={TONE_VARIANT[badge.tone]}>{badge.label}</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {STREAM_LABELS[s.stream].detail}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          Last success {ago(s.lastSuccessAt)}
                          {s.nextDueAt ? ` · next ${ago(s.nextDueAt)}` : ' · on demand'}
                        </div>
                        {s.lastError && (
                          <div className="mt-1 text-xs text-destructive">
                            {ehrErrorMessage(s.lastError.code, s.lastError.message) ??
                              s.lastError.message}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={s.status === 'running' || pauseMutation.isPending}
                        onClick={() =>
                          pauseMutation.mutate({ stream: s.stream, enabled: !s.enabled })
                        }
                      >
                        {s.enabled ? (
                          <>
                            <Pause className="mr-1 h-3.5 w-3.5" /> Pause
                          </>
                        ) : (
                          <>
                            <Play className="mr-1 h-3.5 w-3.5" /> Resume
                          </>
                        )}
                      </Button>
                    </div>
                  );
                })}
              </section>

              {(subscriptions ?? []).length > 0 && (
                <section className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium">Change-feed subscriptions</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={checkMutation.isPending}
                      onClick={() => checkMutation.mutate()}
                    >
                      {checkMutation.isPending ? (
                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-1 h-3.5 w-3.5" />
                      )}
                      Check
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    The EHR applies a subscribe or unsubscribe 10–20 minutes after it is requested.
                  </p>
                  {(subscriptions ?? []).map((sub) => {
                    const badge = subscriptionBadge(sub);
                    const action = subscriptionAction(sub);
                    return (
                      <div key={sub.feed} className="flex items-start gap-3 rounded-md border p-3">
                        <Rss className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{FEED_LABELS[sub.feed]}</span>
                            <Badge variant={TONE_VARIANT[badge.tone]}>{badge.label}</Badge>
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {sub.events.length > 0 ? `${sub.events.length} events · ` : ''}checked{' '}
                            {ago(sub.observedAt)}
                            {sub.requestedAt
                              ? ` · last change requested ${ago(sub.requestedAt)}`
                              : ''}
                          </div>
                          {sub.error && (
                            <div className="mt-1 text-xs text-destructive">
                              {ehrErrorMessage(sub.error.code, sub.error.message) ??
                                sub.error.message}
                            </div>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={action.disabled || subscribeMutation.isPending}
                          onClick={() =>
                            action.desired === 'unsubscribed'
                              ? setConfirmUnsubscribe(sub)
                              : subscribeMutation.mutate({
                                  feed: sub.feed,
                                  desired: action.desired,
                                })
                          }
                        >
                          {action.label}
                        </Button>
                      </div>
                    );
                  })}
                </section>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => runMutation.mutate([...chosen])}
              disabled={chosen.size === 0 || runMutation.isPending}
            >
              {runMutation.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              )}
              Sync selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmUnsubscribe !== null}
        onOpenChange={(o) => {
          if (!o) setConfirmUnsubscribe(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Unsubscribe from{' '}
              {confirmUnsubscribe ? FEED_LABELS[confirmUnsubscribe.feed].toLowerCase() : ''}{' '}
              changes?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Changes made in the EHR while unsubscribed never reach VagusX. Subscribing again needs
              a catch-up read to fill the gap.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep subscribed</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmUnsubscribe) {
                  subscribeMutation.mutate({
                    feed: confirmUnsubscribe.feed,
                    desired: 'unsubscribed',
                  });
                }
                setConfirmUnsubscribe(null);
              }}
            >
              Unsubscribe
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
